import { useCallback, useEffect, useState } from 'react';
import type { DropEvent } from 'react-aria';

import { fileUploadService, getUploadErrorMessage } from '@/api/fileUploadApi';

export async function validateImageUploadFile(file: File): Promise<{
	previewUrl: string | null;
	error?: string;
}> {
	return fileUploadService.createImagePreview(file);
}

export function useImageUpload(userId: string | undefined) {
	const [preview, setPreview] = useState<string | null>(null);
	const [url, setUrl] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleFile = useCallback(
		async (file: File): Promise<string | null> => {
			if (!userId) return null;

			setIsUploading(true);
			setError(null);

			try {
				const { previewUrl, error: previewError } =
					await validateImageUploadFile(file);
				if (previewError || !previewUrl) {
					console.error('Preview error:', previewError);
					setError(previewError ?? 'Upload failed. Please try again.');
					return null;
				}
				setPreview(previewUrl);

				const result = await fileUploadService.upload(file, userId);
				setUrl(result.url);
				return result.url;
			} catch (uploadError) {
				console.error('Upload error:', uploadError);
				setError(getUploadErrorMessage(uploadError));
				return null;
			} finally {
				setIsUploading(false);
			}
		},
		[userId],
	);

	const handleDrop = useCallback(
		async (e: DropEvent) => {
			const item = e.items[0];
			if (!item || item.kind !== 'file') return;
			const file = await item.getFile();
			await handleFile(file);
		},
		[handleFile],
	);

	const reset = useCallback(() => {
		setPreview(null);
		setUrl(null);
		setError(null);
	}, []);

	return { preview, url, isUploading, error, handleFile, handleDrop, reset };
}

export function useImageUploadReset(
	imageUpload: ReturnType<typeof useImageUpload>,
	serverImage: string | null | undefined,
) {
	useEffect(() => {
		imageUpload.reset();
		// Only reset when serverImage changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serverImage]);
}
